/**
 * Customer-facing stock limits for catalogue products (detail page, cart).
 * Aligns with ProductItem and Firestore/CSV shape: stock 'x' = unlimited, number = cap, empty = legacy uncapped.
 */
import unifiedStockService from '../services/unifiedStockService';

const LEGACY_UNCAP_QTY = 99999;

/**
 * @param {object|null} product
 * @param {{ reserved?: number }} [options]
 * @returns {{
 *   isUnlimited: boolean,
 *   numericStock: number,
 *   maxOrderable: number,
 *   canPurchase: boolean,
 * }}
 */
export function getCustomerStockConstraints(product, options = {}) {
    const reserved = Math.max(0, Number(options.reserved) || 0);

    if (!product) {
        return {
            isUnlimited: false,
            numericStock: 0,
            maxOrderable: 0,
            canPurchase: false,
        };
    }

    if (product.stock === 'x') {
        const ok = product.inStock !== false;
        return {
            isUnlimited: true,
            numericStock: LEGACY_UNCAP_QTY,
            maxOrderable: LEGACY_UNCAP_QTY,
            canPurchase: ok,
        };
    }

    const raw = product.stock;

    if (raw === undefined || raw === null || raw === '') {
        const ok = product.inStock !== false;
        return {
            isUnlimited: true,
            numericStock: LEGACY_UNCAP_QTY,
            maxOrderable: LEGACY_UNCAP_QTY,
            canPurchase: ok,
        };
    }

    let numeric = 0;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        numeric = Math.max(0, Math.floor(raw));
    } else if (typeof raw === 'string' && /^\s*\d+\s*$/.test(raw)) {
        numeric = Math.max(0, parseInt(raw.trim(), 10));
    } else {
        numeric = Math.max(0, Math.floor(unifiedStockService.extractStockValue(raw)));
    }

    const maxOrderable = Math.max(0, numeric - reserved);
    const canPurchase = maxOrderable > 0 && product.inStock !== false;

    return {
        isUnlimited: false,
        numericStock: numeric,
        maxOrderable,
        canPurchase,
    };
}

/**
 * How many units may still be added for this SKU given quantity already in cart.
 */
export function getRemainingAddable(product, quantityAlreadyInCart, options = {}) {
    const { isUnlimited, maxOrderable } = getCustomerStockConstraints(product, options);
    const inCart = Math.max(0, Number(quantityAlreadyInCart) || 0);
    if (isUnlimited) return LEGACY_UNCAP_QTY;
    return Math.max(0, maxOrderable - inCart);
}
